import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CampaignLanding.css';

const CampaignLanding = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadAvailableCampaigns();
  }, []);

  const loadAvailableCampaigns = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load the campaigns.json file to get list of available campaigns
      const campaignsResponse = await fetch(`${process.env.PUBLIC_URL || ''}/campaigns.json`);
      
      if (!campaignsResponse.ok) {
        throw new Error('Could not load campaigns.json');
      }
      
      const campaignIds = await campaignsResponse.json();
      const availableCampaigns = [];

      // Load data for each campaign listed in campaigns.json
      for (const campaignId of campaignIds) {
        try {
          // Try to load campaign configuration and content files
          const [contentResponse, routesResponse, vehiclesResponse] = await Promise.all([
            fetch(`${process.env.PUBLIC_URL || ''}/campaigns/${campaignId}/english.json`),
            fetch(`${process.env.PUBLIC_URL || ''}/campaigns/${campaignId}/routes-config.json`),
            fetch(`${process.env.PUBLIC_URL || ''}/campaigns/${campaignId}/vehicles.json`)
          ]);

          // Only include campaigns that have all required files
          if (contentResponse.ok && routesResponse.ok && vehiclesResponse.ok) {
            const [contentData, routesData, vehiclesData] = await Promise.all([
              contentResponse.json(),
              routesResponse.json(),
              vehiclesResponse.json()
            ]);

            // Build campaign object from discovered data
            const routeIds = Array.isArray(routesData) ? routesData.map(route => route.id) : [];
            
            const campaign = {
              id: campaignId,
              name: contentData.title || contentData.name || `Campaign ${campaignId.toUpperCase()}`,
              description: contentData.description || `SFMTA Campaign featuring routes ${routeIds.join(', ') || 'various'}`,
              routes: routeIds,
              status: 'active',
              vehicleCount: Array.isArray(vehiclesData) ? vehiclesData.length : (vehiclesData.vehicles?.length || 0),
              lastUpdated: contentData.lastUpdated || new Date().toISOString().split('T')[0]
            };

            availableCampaigns.push(campaign);
            console.log(`Discovered campaign: ${campaignId}`, campaign);
          }
        } catch (err) {
          console.warn(`Failed to load campaign ${campaignId}:`, err);
        }
      }

      setCampaigns(availableCampaigns);
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError('Failed to load available campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignSelect = (campaignId) => {
    navigate(`/${campaignId}`);
  };

  if (loading) {
    return (
      <div className="campaign-landing">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading available campaigns...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="campaign-landing">
        <div className="error-container">
          <h2>Error Loading Campaigns</h2>
          <p>{error}</p>
          <button onClick={loadAvailableCampaigns} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-landing">
      <header className="landing-header">
        <h1>SFMTA Campaign Transit Map</h1>
        <p>Select a campaign to view real-time transit data</p>
      </header>

      <div className="campaigns-grid">
        {campaigns.map((campaign) => (
          <div 
            key={campaign.id} 
            className="campaign-card"
            onClick={() => handleCampaignSelect(campaign.id)}
          >
            <div className="campaign-header">
              <h3>{campaign.name}</h3>
              <span className={`status-badge ${campaign.status}`}>
                {campaign.status}
              </span>
            </div>
            
            <p className="campaign-description">{campaign.description}</p>
            
            <div className="campaign-details">
              <div className="detail-item">
                <span className="label">Routes:</span>
                <span className="value">{campaign.routes.join(', ')}</span>
              </div>
              <div className="detail-item">
                <span className="label">Vehicles:</span>
                <span className="value">{campaign.vehicleCount}</span>
              </div>
            </div>

            <div className="campaign-footer">
              <button className="select-button">
                View Campaign →
              </button>
            </div>
          </div>
        ))}
      </div>

      {campaigns.length === 0 && (
        <div className="no-campaigns">
          <h3>No campaigns available</h3>
          <p>Check back later for new campaigns or contact the administrator.</p>
        </div>
      )}

      <footer className="landing-footer">
        <p>
          Real-time transit data powered by UMO IQ API • 
          Built with React + Leaflet
        </p>
      </footer>
    </div>
  );
};

export default CampaignLanding;
